import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail } from 'lucide-react';

const Technology = () => {
  // Images
  const images = {
    professional1: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/fra8uhtu_4f6348a5-d706-485f-b759-e3614338175e.JPG",
    professional2: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/c8h45aqk_20abfd5f-c702-4abf-86af-638ab408f020.JPG",
    classPhoto: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/vivz40da_IMG_3351.JPG",
    teaching: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/w2xxbqc1_IMG_3340.JPG",
    alknzPlatform: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/uddtp1ty_Screen%20Shot%202026-02-09%20at%206.05.04%20PM.png"
  };

  const technicalDisciplines = [
    "Software Engineering",
    "Data Science",
    "Data Analytics",
    "Cybersecurity",
    "Technical career readiness and applied problem-solving"
  ];

  const universities = [
    "University of Texas",
    "Columbia University",
    "University of California (multiple campuses)",
    "University of Washington",
    "University of Denver",
    "University of North Carolina",
    "George Washington University",
    "Rutgers University",
    "Northwestern University",
    "University of Toronto",
    "University of Sydney"
  ];

  const careerAreas = [
    "Technology",
    "Data",
    "Cybersecurity",
    "Product and analytics-adjacent roles"
  ];

  const careerSupport = [
    "Break into tech from non-technical backgrounds",
    "Secure jobs in the United States and abroad",
    "Relocate internationally — particularly to the Middle East and GCC",
    "Navigate visas, employer expectations, and cross-cultural professional norms"
  ];

  const ventureWork = [
    "Data architecture and internal systems",
    "Research infrastructure",
    "Digital platforms and portals",
    "Strategic tooling to support venture decision-making"
  ];

  const chromeExtensionFeatures = [
    {
      title: "Capture Intelligence",
      description: "Scrapes visible and structured information from the active browser tab. Captures entity names, job titles, affiliations, contact signals, and geographic indicators."
    },
    {
      title: "Deep AI Enrichment",
      description: "Analyzes captured text to extract normalized fields, classifications, and summaries. Generates clean entity names, role normalization, and relevance signals."
    },
    {
      title: "Smart Merge Rules",
      description: "Conservative merge logic that only fills empty fields, deduplicates contacts, and appends sources — never overwrites existing data."
    },
    {
      title: "Secure Upload",
      description: "Sends final payload to backend with captured fields, AI enrichment, merge flags, and timestamps. The only step that mutates backend data."
    }
  ];

  const consultingServices = [
    "Application development (internal tools, platforms, MVPs)",
    "Website design and build (strategy-led, not template-driven)",
    "Technical consulting for startups, funds, and founders",
    "Data-driven systems for scale and operations"
  ];

  return (
    <div className="bg-black pt-24 lg:pt-32">
      {/* Hero Section */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-6">
                Technology
              </p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white font-normal leading-[0.95] tracking-tight">
                Building Systems, Not Just Skills
              </h1>
              <p className="font-serif text-xl md:text-2xl text-white/80 italic leading-relaxed mt-8">
                Technology has never been just about code for me — it's about mobility, access, and power.
              </p>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-6">
                For over a decade, I've worked at the intersection of technical education, global careers, and infrastructure, helping individuals and institutions move from potential to execution.
              </p>
            </div>
            <div>
              <img
                src={images.professional1}
                alt="Mariam Sallam"
                className="w-full aspect-square object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Teaching & Technical Education */}
      <section className="py-14 md:py-24 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Education
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
                Teaching & Technical Education
              </h2>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-8">
                I have taught and designed programs across core technical disciplines, including:
              </p>
              
              <div className="mt-8 space-y-0">
                {technicalDisciplines.map((item, index) => (
                  <div key={index} className="py-4 border-t border-white/10 first:border-t-0">
                    <p className="font-sans text-base text-white/80">{item}</p>
                  </div>
                ))}
              </div>

              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-8">
                My work focused not only on curriculum delivery, but on translating complex technical concepts into real-world, job-ready capability — especially for career switchers and international students.
              </p>
            </div>

            <div>
              <img
                src={images.teaching}
                alt="Teaching environment"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* University Partnerships */}
      <section className="py-14 md:py-24 lg:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="lg:order-2">
              <img
                src={images.classPhoto}
                alt="University class"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
            <div className="lg:order-1">
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-black/40 mb-4">
                Partnerships
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-black font-normal tracking-tight">
                University Partnerships
              </h2>
              <p className="font-sans text-sm text-black/50 mt-2">2U Network</p>
              
              <p className="font-sans text-base text-black/60 leading-[1.8] font-light mt-8">
                I worked across universities powered by 2U (formerly Trilogy / edX bootcamps), supporting technical education at scale.
              </p>
              <p className="font-sans text-base text-black/60 leading-[1.8] font-light mt-4">
                This includes programs delivered in partnership with leading institutions such as:
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {universities.map((uni, index) => (
                  <p key={index} className="font-sans text-sm text-black/70">{uni}</p>
                ))}
                <p className="font-sans text-sm text-black/50 italic col-span-full mt-2">
                  (and additional global university partners within the 2U ecosystem)
                </p>
              </div>

              <p className="font-sans text-base text-black/60 leading-[1.8] font-light mt-8">
                Across these institutions, I supported learners navigating rigorous technical programs while preparing for competitive global job markets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Career Coaching & Global Mobility */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Career Strategy
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
                Career Coaching & Global Mobility
              </h2>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-8">
                Beyond teaching, my work has been deeply rooted in career strategy.
              </p>
              
              {/* Stats */}
              <div className="mt-10 py-8 border-t border-b border-white/10">
                <span className="font-serif text-5xl lg:text-6xl text-white">5,000+</span>
                <p className="font-sans text-sm text-white/50 mt-2">
                  professionals coached into securing roles across:
                </p>
                <div className="flex flex-wrap gap-4 mt-4">
                  {careerAreas.map((area, index) => (
                    <span key={index} className="font-sans text-sm text-white/70 px-3 py-1 border border-white/20">
                      {area}
                    </span>
                  ))}
                </div>
              </div>

              <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-white/40 mt-10 mb-4">
                This includes helping individuals:
              </p>
              <div className="space-y-3">
                {careerSupport.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-white/40 rounded-full mt-2 flex-shrink-0" />
                    <p className="font-sans text-sm text-white/70">{item}</p>
                  </div>
                ))}
              </div>

              <p className="font-serif text-lg text-white italic mt-10">
                Career outcomes were never treated as abstract success metrics — they were treated as life transitions.
              </p>
            </div>

            <div>
              <img
                src={images.professional2}
                alt="Professional Mariam Sallam"
                className="w-full aspect-[3/4] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Venture, Data & Infrastructure */}
      <section className="py-14 md:py-24 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Current Focus
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
                Venture, Data & Infrastructure
              </h2>
              <p className="font-serif text-xl text-white/80 italic mt-6">
                Today, my work sits firmly at the intersection of technology and capital.
              </p>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-6">
                In partnership with ALKNZ Ventures, I've helped build the data, research, and internal infrastructure that supports their investment and operating strategy.
              </p>

              <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-white/40 mt-10 mb-6">
                This includes:
              </p>
              <div className="space-y-0">
                {ventureWork.map((item, index) => (
                  <div key={index} className="py-4 border-t border-white/10 first:border-t-0">
                    <p className="font-sans text-base text-white/80">{item}</p>
                  </div>
                ))}
              </div>

              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-10">
                This work extends beyond advisory — it is hands-on system building, aligned with how modern venture and operating companies function.
              </p>

              {/* Links */}
              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <a
                  href="https://www.alknzventures.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] text-white font-sans text-[11px] tracking-[0.15em] uppercase hover:from-[#8B2020] hover:to-[#5A1010] transition-all"
                >
                  Visit ALKNZ Ventures
                  <ExternalLink size={14} />
                </a>
                <a
                  href="mailto:mariam@alknzventures.com"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 text-white font-sans text-[11px] tracking-[0.15em] uppercase hover:bg-white hover:text-black transition-all"
                >
                  <Mail size={14} />
                  mariam@alknzventures.com
                </a>
              </div>
            </div>

            {/* ALKNZ Platform Screenshot */}
            <div>
              <img
                src={images.alknzPlatform}
                alt="ALKNZ Internal Platform"
                className="w-full h-auto border border-white/10"
              />
              <p className="font-sans text-xs text-white/40 mt-3">
                Internal platform built for ALKNZ Ventures
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ALKNZ Chrome Extension */}
      <section className="py-14 md:py-24 lg:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-black/40 mb-4">
              Innovation
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-black font-normal tracking-tight">
              ALKNZ Chrome Extension
            </h2>
            <p className="font-serif text-lg text-black/70 italic mt-4">
              The first-ever Chrome extension that grabs and parses data in Arabic.
            </p>
            <p className="font-sans text-base text-black/60 leading-[1.8] font-light mt-6 max-w-3xl">
              Built to support intelligence gathering and research workflows, this extension captures, enriches, and uploads structured data from web sources — with full Arabic language support.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {chromeExtensionFeatures.map((feature, index) => (
              <div key={index} className="p-8 bg-white border border-black/5">
                <span className="font-serif text-3xl text-black/10">0{index + 1}</span>
                <h3 className="font-serif text-xl text-black tracking-tight mt-4">
                  {feature.title}
                </h3>
                <p className="font-sans text-sm text-black/60 leading-relaxed mt-3 font-light">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consulting, Apps & Digital Products */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Services
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
                Consulting, Apps & Digital Products
              </h2>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-8">
                In addition to institutional and venture work, I provide select consulting and build services, including:
              </p>

              <div className="mt-8 space-y-0">
                {consultingServices.map((item, index) => (
                  <div key={index} className="py-5 border-t border-white/10 first:border-t-0">
                    <p className="font-sans text-base text-white/70">{item}</p>
                  </div>
                ))}
              </div>

              <p className="font-serif text-lg text-white italic mt-10">
                Projects are approached with the same philosophy that defines all my work: clarity, structure, and long-term thinking.
              </p>

              {/* Project Links */}
              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <a
                  href="https://editorial-fashion-1.preview.emergentagent.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 text-white font-sans text-[11px] tracking-[0.15em] uppercase hover:bg-white hover:text-black transition-all"
                >
                  View Sample Project
                  <ExternalLink size={14} />
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] text-white font-sans text-[11px] tracking-[0.15em] uppercase hover:from-[#8B2020] hover:to-[#5A1010] transition-all"
                >
                  Discuss a Project
                </Link>
              </div>
            </div>

            <div className="flex items-center">
              <div className="w-full">
                <video
                  controls
                  loop
                  playsInline
                  className="w-full h-auto border border-white/10"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_editorial-fashion-1/artifacts/8tzbhxtg_Mariam_Sallam_Data_Gov_Video_Test_-_ar.mp4" 
                    type="video/mp4" 
                  />
                </video>
                <p className="font-sans text-xs text-white/40 mt-3">
                  Strategy-Led Development
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-14 md:py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-6">
              Philosophy
            </p>
            <p className="font-serif text-2xl md:text-3xl text-white font-normal leading-[1.3] tracking-tight">
              Technology is not neutral.
            </p>
            <p className="font-serif text-2xl md:text-3xl text-white/70 font-normal leading-[1.3] tracking-tight mt-2">
              It shapes who moves forward — and who gets left behind.
            </p>
            <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-10 max-w-2xl mx-auto">
              My work exists to ensure that systems are built intentionally, careers are built strategically, and infrastructure serves people — not the other way around.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Technology;
